import PropTypes from 'prop-types';
import { useFieldArray, useWatch } from 'react-hook-form';
import { PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import ListFieldMobileLabel from '#/modules/commonComponents/form/ListFieldMobileLabel';
import PlainRegisteredInput from '#/modules/commonComponents/form/PlainRegisteredInput';
import { formatFmsAmount, roundFmsAmount } from '#/modules/finMgmt/shared/helper/fmsAmount';
import { createEmptyPaymentRequestLine } from '#/modules/finMgmt/claims/schemas/paymentRequestFormSchema';

/** 品項｜用途｜單價｜數量｜金額｜移除（對齊物料採購單明細的 list 版型） */
// eslint-disable-next-line max-len -- Tailwind arbitrary grid 須單一完整 class 字面量
const LINE_GRID_COLS = '@xl/form-section:grid-cols-[minmax(10rem,1.4fr)_minmax(10rem,1.4fr)_minmax(6.5rem,7rem)_minmax(5.5rem,6rem)_minmax(7rem,8rem)_2.5rem]';

const LINES_MIN_W = cn('w-full', '@xl/form-section:min-w-[48rem]');

const LINE_HEADER_CLASS = cn(
  'sticky top-0 z-10 hidden w-full gap-3 border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  '@xl/form-section:grid',
  LINE_GRID_COLS,
);

const LINE_ROW_CLASS = cn(
  'grid w-full grid-cols-1 items-center gap-3 px-3 py-3',
  LINE_GRID_COLS,
);

/** 窄螢幕堆疊時才顯示小標；寬螢幕靠表頭 */
const STACK_LABEL_CLASS = '@xl/form-section:hidden';

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

const PaymentRequestLinesEditor = ({
  control,
  register,
  errors,
  readOnly = false,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'lines',
  });
  const [watchedLines, roundingMode] = useWatch({
    control,
    name: ['lines', 'roundingMode'],
  });
  // 對齊後端：每列單價×數量加總後，最後才對整張套用取整方式
  const lineAmounts = (watchedLines ?? []).map(
    (line) => toNumber(line?.unitPrice) * toNumber(line?.qty),
  );
  const estimatedTotal = roundFmsAmount(
    lineAmounts.reduce((sum, amount) => sum + amount, 0),
    roundingMode,
  );
  const linesRootMessage = typeof errors?.lines?.message === 'string'
    ? errors.lines.message
    : null;

  return (
    <section className="@container/form-section">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="form-block__title mb-0!">明細</h2>
        {!readOnly ? (
          <Button
            type="button"
            size="sm"
            onClick={() => append(createEmptyPaymentRequestLine())}
          >
            新增明細
          </Button>
        ) : null}
      </div>

      {linesRootMessage ? (
        <p className="mb-3 text-sm text-red-500">{linesRootMessage}</p>
      ) : null}

      {/* 外層負責橫向捲動；內層固定最小寬，視窗收縮時整張列表可左右滑動，單價／數量不被壓扁 */}
      <div className="create-edit-panel-scroll w-full max-w-full overflow-x-auto rounded-md border border-gray-300">
        <div className={LINES_MIN_W}>
          <div className={LINE_HEADER_CLASS} aria-hidden>
            <span>品項</span>
            <span>用途</span>
            <span>單價</span>
            <span>數量</span>
            <span className="text-right">金額</span>
            <span className="sr-only">操作</span>
          </div>
          {fields.length === 0 ? (
            <p className="m-0 px-3 py-6 text-center text-sm text-muted-foreground">尚未新增明細</p>
          ) : (
            <ul className="m-0 list-none divide-y divide-gray-200 p-0">
              {fields.map((field, index) => (
                <li key={field.id} className={LINE_ROW_CLASS}>
                  <div className="min-w-0">
                    <PlainRegisteredInput
                      id={`lines.${index}.itemName`}
                      register={register}
                      errors={errors}
                      mobileLabel="品項"
                      mobileLabelClassName={STACK_LABEL_CLASS}
                      ariaLabel={`品項 ${index + 1}`}
                      readOnly={readOnly}
                    />
                  </div>
                  <div className="min-w-0">
                    <PlainRegisteredInput
                      id={`lines.${index}.purpose`}
                      register={register}
                      errors={errors}
                      mobileLabel="用途"
                      mobileLabelClassName={STACK_LABEL_CLASS}
                      ariaLabel={`用途 ${index + 1}`}
                      readOnly={readOnly}
                    />
                  </div>
                  <div className="min-w-0">
                    <PlainRegisteredInput
                      id={`lines.${index}.unitPrice`}
                      register={register}
                      errors={errors}
                      type="number"
                      min="0"
                      step="0.01"
                      mobileLabel="單價"
                      mobileLabelClassName={STACK_LABEL_CLASS}
                      ariaLabel={`單價 ${index + 1}`}
                      readOnly={readOnly}
                    />
                  </div>
                  <div className="min-w-0">
                    <PlainRegisteredInput
                      id={`lines.${index}.qty`}
                      register={register}
                      errors={errors}
                      type="number"
                      min="1"
                      step="1"
                      mobileLabel="數量"
                      mobileLabelClassName={STACK_LABEL_CLASS}
                      ariaLabel={`數量 ${index + 1}`}
                      readOnly={readOnly}
                    />
                  </div>
                  <div className="min-w-0">
                    <ListFieldMobileLabel className={STACK_LABEL_CLASS}>金額</ListFieldMobileLabel>
                    <p className="m-0 py-2 text-sm tabular-nums @xl/form-section:text-right">
                      {formatFmsAmount(lineAmounts[index] ?? 0)}
                    </p>
                  </div>
                  {/* 寬螢幕與輸入框（h-12.5）同高並垂直置中 */}
                  <div className="flex min-w-0 items-center @xl/form-section:h-12.5 @xl/form-section:justify-end">
                    {!readOnly ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                        aria-label={`移除明細 ${index + 1}`}
                        onClick={() => remove(index)}
                      >
                        <PiTrashBold className="size-4" />
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-col items-end gap-0.5">
        <p className="m-0 text-sm">
          <span className="text-gray-600">預估合計</span>
          <span className="ml-3 text-base font-semibold tabular-nums">
            {formatFmsAmount(estimatedTotal)}
          </span>
        </p>
        <p className="m-0 text-xs text-muted-foreground">實際金額以送出後系統計算為準</p>
      </div>
    </section>
  );
};

PaymentRequestLinesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
};

export default PaymentRequestLinesEditor;
